import moment from 'moment';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { DateTime, ExportDropdown, HelpTooltip, Inline, Percent, PeriodFilter, YesNoFilter } from '~/components';
import FilterButton from '~/components/filters/FilterButton';
import MemberFiltersBar from '~/components/filters/MemberFiltersBar';
import MemberFiltersGroup from '~/components/filters/MemberFiltersGroup';
import SingleSelectFilter from '~/components/filters/SingleSelectFilter';
import { useApi, useConfirmation, useWorkspace } from '~/contexts';
import { useDocumentTitle, useSearchParams, useSearchParamsConfig } from '~/hooks';
import useMemberFilters from '~/hooks/useMemberFilters';
import { PageLoader } from '~/routes/public/pages';
import { colors, weights } from '~/styles';
import { dateFormats, intervalOptions, mimeTypes, sortAlphabetically } from '~/utils';
import ExportDialogAsync from '../components/ExportDialogAsync';
import List from '../components/List';
import ListTooltip from '../components/ListTooltip';
import Report from '../components/Report';
import Table from '../components/table';
import ReportActions from '../ReportActions';

const intervalsByScope = {
  get week() {
    return [
      intervalOptions.custom,
      intervalOptions.past_4_weeks,
      intervalOptions.past_12_weeks,
      intervalOptions.past_26_weeks,
    ];
  },

  get month() {
    return [
      intervalOptions.custom,
      intervalOptions.past_3_months,
      intervalOptions.past_6_months,
      intervalOptions.past_9_months,
      intervalOptions.past_12_months,
    ];
  },
};

export default function UtilizationVarianceByMember({ report }) {
  useDocumentTitle(report.name);
  const { workspace } = useWorkspace();
  const api = useApi();

  const searchParamsConfig = useSearchParamsConfig();

  const [query, setQuery] = useState({ report: null, status: 'loading' });

  const memberFilters = useMemberFilters({
    memberStatusId: { ...searchParamsConfig.memberStatusId, default: 'active' },
  });

  const [params, setParams] = useState({
    period: null,
    unit: '',

    metric: 'billable_utilization_variance',

    utilizationMode: 'default',

    ...memberFilters.filters,
  });

  const [searchParamsStatus, setSearchParamsStatus] = useState('pending');
  const searchParams = useSearchParams({
    config: useMemo(
      () => ({
        period: {
          ...searchParamsConfig.periodByUnit,
          default: intervalOptions.past_6_months,
          intervals: (unit) => intervalsByScope[unit],
          defaults: {
            day: intervalOptions.past_30_days,
            week: intervalOptions.past_12_weeks,
            month: intervalOptions.past_6_months,
          },
        },
        unit: { default: 'month', valid: ['week', 'month'] },

        ...memberFilters.searchParamsConfig,

        metric: {
          default: 'billable_utilization_variance',
          valid: [
            'billable_utilization_variance',
            'client_utilization_variance',
            'productive_utilization_variance',
            'total_utilization_variance',
            'target_attainment_variance',
          ],
        },

        utilizationMode: {
          valid: ['default', 'time_off_adjusted'],
          default: workspace.defaultAdjustForTimeOff ? 'time_off_adjusted' : 'default',
        },
      }),
      [searchParamsConfig, memberFilters, workspace.defaultAdjustForTimeOff],
    ),
    onChange: (params) => setParams((state) => ({ ...state, ...params })),
  });

  // Map the values to perform the API query
  const urlSearchParams = useMemo(
    () => ({
      start: params.period?.start ?? undefined,
      end: params.period?.end ?? undefined,
      unit: params.unit,

      ...memberFilters.mapUrlSearchParams(params),
    }),
    [params, memberFilters],
  );

  useEffect(() => {
    if (searchParamsStatus === 'ready') return;
    searchParams.get().then((params) => {
      setParams((state) => ({ ...state, ...params }));
      setSearchParamsStatus('ready');
    });
  }, [searchParams, searchParamsStatus]);

  const fetchData = useCallback(async () => {
    const { data } = await api.www.workspaces(workspace.id).reports(report.id).data(urlSearchParams);
    setQuery((state) => ({ ...state, data, status: 'ready' }));
  }, [api, workspace.id, report.id, urlSearchParams]);

  useEffect(() => {
    if (searchParamsStatus !== 'ready') return;
    fetchData();
  }, [fetchData, searchParamsStatus]);

  const [filtersVisible, setFiltersVisible] = useState(false);
  const showFilters = () => setFiltersVisible(true);
  const hideFilters = () => setFiltersVisible(false);
  const handleApplyFilters = (values) => {
    if (values !== params) setQuery((state) => ({ ...state, status: 'filtering' }));

    setParams({ ...params, ...values });
    searchParams.set(values);
    hideFilters();
  };

  const handleMetricChange = (metric) => {
    setParams({ ...params, metric });
    searchParams.set({ metric });
  };

  const handleUtilizationModeChange = (utilizationMode) => {
    setParams({ ...params, utilizationMode });
    searchParams.set({ utilizationMode });
  };

  const confirmation = useConfirmation();

  const handleExport = (mimeType) => {
    confirmation.prompt((resolve) => (
      <ExportDialogAsync
        onLoad={api.www
          .workspaces(workspace.id)
          .reports(report.id)
          .data(urlSearchParams, {
            headers: { accept: mimeType },
          })}
        onClose={resolve}
      />
    ));
  };

  return (
    <Report>
      <Report.Header>
        <Report.Info report={report} />

        <Report.Actions>
          <ReportActions onSave={() => searchParams.set(params)} />
          <ExportDropdown>
            {({ setIsOpen }) => (
              <>
                <ExportDropdown.Item
                  onClick={async () => {
                    await handleExport(mimeTypes.csv);
                    setIsOpen(false);
                  }}>
                  Export to CSV
                </ExportDropdown.Item>

                <ExportDropdown.Item
                  onClick={async () => {
                    await handleExport(mimeTypes.xlsx);
                    setIsOpen(false);
                  }}>
                  Export to Excel
                </ExportDropdown.Item>
              </>
            )}
          </ExportDropdown>

          <FilterButton isOutline onClick={showFilters} />
        </Report.Actions>
      </Report.Header>

      <Report.FiltersBar>
        <PeriodFilter
          clearable={false}
          scope={params.unit}
          intervals={intervalsByScope[params.unit]}
          placeholder="Date Range"
          value={params.period}
          onChange={({ target: { value } }) => handleApplyFilters({ period: value })}
        />

        <UnitFilter
          value={params.unit}
          onChange={({ target: { value } }) =>
            handleApplyFilters({
              unit: value,
              period: {
                day: intervalOptions.past_30_days,
                week: intervalOptions.past_12_weeks,
                month: intervalOptions.past_6_months,
              }[value],
            })
          }
        />

        <Inline>
          <YesNoFilter
            icon="gear"
            placeholder="Adjust for Paid Time Off"
            value={params.utilizationMode === 'time_off_adjusted' ? 'yes' : 'no'}
            onChange={({ target: { value } }) =>
              handleUtilizationModeChange(value === 'yes' ? 'time_off_adjusted' : 'default')
            }
          />
          <HelpTooltip
            style={{ marginLeft: '.5rem' }}
            message="Reduces the capacity by the number of Time Off hours taken during the period."
          />
        </Inline>

        <MetricFilter value={params.metric} onChange={({ value }) => handleMetricChange(value)} />

        <MemberFiltersBar filters={params} onChange={handleApplyFilters} />
      </Report.FiltersBar>

      {(() => {
        switch (query.status) {
          case 'loading':
          case 'filtering':
            return <PageLoader />;

          default:
            return (
              <>
                <Data query={query} params={params} />
                <Filters values={params} isOpen={filtersVisible} onApply={handleApplyFilters} onClose={hideFilters} />
              </>
            );
        }
      })()}
    </Report>
  );
}

function Data({ query, params }) {
  const report = query.data;

  const { members, records, periods } = useMemo(() => {
    const members = sortAlphabetically(report.members, 'name');

    const records = report.records.reduce(
      (a, v) => {
        const metrics = {
          default: {
            actualCapacity: v.actualCapacity,
            actualTargetBillableHours: v.actualTargetBillableHours,
            actualBillableUtilization: v.actualBillableUtilization,
            actualClientUtilization: v.actualClientUtilization,
            actualProductiveUtilization: v.actualProductiveUtilization,
            actualTotalUtilization: v.actualTotalUtilization,
            actualTargetAttainment: v.actualTargetAttainment,
            plannedCapacity: v.plannedCapacity,
            plannedTargetBillableHours: v.plannedTargetBillableHours,
            plannedBillableUtilization: v.plannedBillableUtilization,
            plannedClientUtilization: v.plannedClientUtilization,
            plannedProductiveUtilization: v.plannedProductiveUtilization,
            plannedTotalUtilization: v.plannedTotalUtilization,
            plannedTargetAttainment: v.plannedTargetAttainment,
            billableUtilizationVariance: v.billableUtilizationVariance,
            clientUtilizationVariance: v.clientUtilizationVariance,
            productiveUtilizationVariance: v.productiveUtilizationVariance,
            totalUtilizationVariance: v.totalUtilizationVariance,
            targetAttainmentVariance: v.targetAttainmentVariance,
            billableUtilizationVarianceRatio: v.billableUtilizationVarianceRatio,
            clientUtilizationVarianceRatio: v.clientUtilizationVarianceRatio,
            productiveUtilizationVarianceRatio: v.productiveUtilizationVarianceRatio,
            totalUtilizationVarianceRatio: v.totalUtilizationVarianceRatio,
            targetAttainmentVarianceRatio: v.targetAttainmentVarianceRatio,
          },
          time_off_adjusted: {
            actualCapacity: v.actualCapacityTimeOffAdjusted,
            actualTargetBillableHours: v.actualTargetBillableHoursTimeOffAdjusted,
            actualBillableUtilization: v.actualBillableUtilizationTimeOffAdjusted,
            actualClientUtilization: v.actualClientUtilizationTimeOffAdjusted,
            actualProductiveUtilization: v.actualProductiveUtilizationTimeOffAdjusted,
            actualTotalUtilization: v.actualTotalUtilizationTimeOffAdjusted,
            actualTargetAttainment: v.actualTargetAttainmentTimeOffAdjusted,
            plannedCapacity: v.plannedCapacityTimeOffAdjusted,
            plannedTargetBillableHours: v.plannedTargetBillableHoursTimeOffAdjusted,
            plannedBillableUtilization: v.plannedBillableUtilizationTimeOffAdjusted,
            plannedClientUtilization: v.plannedClientUtilizationTimeOffAdjusted,
            plannedProductiveUtilization: v.plannedProductiveUtilizationTimeOffAdjusted,
            plannedTotalUtilization: v.plannedTotalUtilizationTimeOffAdjusted,
            plannedTargetAttainment: v.plannedTargetAttainmentTimeOffAdjusted,
            billableUtilizationVariance: v.billableUtilizationVarianceTimeOffAdjusted,
            clientUtilizationVariance: v.clientUtilizationVarianceTimeOffAdjusted,
            productiveUtilizationVariance: v.productiveUtilizationVarianceTimeOffAdjusted,
            totalUtilizationVariance: v.totalUtilizationVarianceTimeOffAdjusted,
            targetAttainmentVariance: v.targetAttainmentVarianceTimeOffAdjusted,
            billableUtilizationVarianceRatio: v.billableUtilizationVarianceRatioTimeOffAdjusted,
            clientUtilizationVarianceRatio: v.clientUtilizationVarianceRatioTimeOffAdjusted,
            productiveUtilizationVarianceRatio: v.productiveUtilizationVarianceRatioTimeOffAdjusted,
            totalUtilizationVarianceRatio: v.totalUtilizationVarianceRatioTimeOffAdjusted,
            targetAttainmentVarianceRatio: v.targetAttainmentVarianceRatioTimeOffAdjusted,
          },
        }[params.utilizationMode];

        a.total.actualCapacity += metrics.actualCapacity || 0;
        a.total.actualTargetBillableHours += metrics.actualTargetBillableHours || 0;
        a.total.actualBillableHours += v.actualBillableHours || 0;
        a.total.actualTotalClientHours += v.actualTotalClientHours || 0;
        a.total.actualTotalProductiveHours += v.actualTotalProductiveHours || 0;
        a.total.actualTotalProjectHours += v.actualTotalProjectHours || 0;

        a.total.plannedCapacity += metrics.plannedCapacity || 0;
        a.total.plannedTargetBillableHours += metrics.plannedTargetBillableHours || 0;
        a.total.plannedBillableHours += v.plannedBillableHours || 0;
        a.total.plannedTotalClientHours += v.plannedTotalClientHours || 0;
        a.total.plannedTotalProductiveHours += v.plannedTotalProductiveHours || 0;
        a.total.plannedTotalProjectHours += v.plannedTotalProjectHours || 0;

        // Members
        a.members[v.memberId] = a.members[v.memberId] || {
          total: {
            actualCapacity: 0,
            actualTargetBillableHours: 0,
            actualBillableHours: 0,
            actualTotalClientHours: 0,
            actualTotalProductiveHours: 0,
            actualTotalProjectHours: 0,
            get actualBillableUtilization() {
              if (!this.actualCapacity) return null;
              return this.actualBillableHours / this.actualCapacity;
            },
            get actualClientUtilization() {
              if (!this.actualCapacity) return null;
              return this.actualTotalClientHours / this.actualCapacity;
            },
            get actualProductiveUtilization() {
              if (!this.actualCapacity) return null;
              return this.actualTotalProductiveHours / this.actualCapacity;
            },
            get actualTotalUtilization() {
              if (!this.actualCapacity) return null;
              return this.actualTotalProjectHours / this.actualCapacity;
            },
            get actualTargetAttainment() {
              if (!this.actualTargetBillableHours) return null;
              return this.actualBillableHours / this.actualTargetBillableHours;
            },

            plannedCapacity: 0,
            plannedTargetBillableHours: 0,
            plannedBillableHours: 0,
            plannedTotalClientHours: 0,
            plannedTotalProductiveHours: 0,
            plannedTotalProjectHours: 0,
            get plannedBillableUtilization() {
              if (!this.plannedCapacity) return null;
              return this.plannedBillableHours / this.plannedCapacity;
            },
            get plannedClientUtilization() {
              if (!this.plannedCapacity) return null;
              return this.plannedTotalClientHours / this.plannedCapacity;
            },
            get plannedProductiveUtilization() {
              if (!this.plannedCapacity) return null;
              return this.plannedTotalProductiveHours / this.plannedCapacity;
            },
            get plannedTotalUtilization() {
              if (!this.plannedCapacity) return null;
              return this.plannedTotalProjectHours / this.plannedCapacity;
            },
            get plannedTargetAttainment() {
              if (!this.plannedTargetBillableHours) return null;
              return this.plannedBillableHours / this.plannedTargetBillableHours;
            },

            get billableUtilizationVariance() {
              return this.actualBillableUtilization - this.plannedBillableUtilization;
            },
            get clientUtilizationVariance() {
              return this.actualClientUtilization - this.plannedClientUtilization;
            },
            get productiveUtilizationVariance() {
              return this.actualProductiveUtilization - this.plannedProductiveUtilization;
            },
            get totalUtilizationVariance() {
              return this.actualTotalUtilization - this.plannedTotalUtilization;
            },
            get targetAttainmentVariance() {
              return this.actualTargetAttainment - this.plannedTargetAttainment;
            },

            get billableUtilizationVarianceRatio() {
              if (!this.plannedBillableUtilization) return null;
              return this.billableUtilizationVariance / this.plannedBillableUtilization;
            },
            get clientUtilizationVarianceRatio() {
              if (!this.plannedClientUtilization) return null;
              return this.clientUtilizationVariance / this.plannedClientUtilization;
            },
            get productiveUtilizationVarianceRatio() {
              if (!this.plannedProductiveUtilization) return null;
              return this.productiveUtilizationVariance / this.plannedProductiveUtilization;
            },
            get totalUtilizationVarianceRatio() {
              if (!this.plannedTotalUtilization) return null;
              return this.totalUtilizationVariance / this.plannedTotalUtilization;
            },
            get targetAttainmentVarianceRatio() {
              if (!this.plannedTargetAttainment) return null;
              return this.targetAttainmentVariance / this.plannedTargetAttainment;
            },
          },
          periods: {},
        };
        a.members[v.memberId].total.actualCapacity += metrics.actualCapacity || 0;
        a.members[v.memberId].total.actualTargetBillableHours += metrics.actualTargetBillableHours || 0;
        a.members[v.memberId].total.actualBillableHours += v.actualBillableHours || 0;
        a.members[v.memberId].total.actualTotalClientHours += v.actualTotalClientHours || 0;
        a.members[v.memberId].total.actualTotalProductiveHours += v.actualTotalProductiveHours || 0;
        a.members[v.memberId].total.actualTotalProjectHours += v.actualTotalProjectHours || 0;
        a.members[v.memberId].total.plannedCapacity += metrics.plannedCapacity || 0;
        a.members[v.memberId].total.plannedTargetBillableHours += metrics.plannedTargetBillableHours || 0;
        a.members[v.memberId].total.plannedBillableHours += v.plannedBillableHours || 0;
        a.members[v.memberId].total.plannedTotalClientHours += v.plannedTotalClientHours || 0;
        a.members[v.memberId].total.plannedTotalProductiveHours += v.plannedTotalProductiveHours || 0;
        a.members[v.memberId].total.plannedTotalProjectHours += v.plannedTotalProjectHours || 0;

        a.members[v.memberId].periods[v.date] = {
          actualCapacity: metrics.actualCapacity,
          actualTargetBillableHours: metrics.actualTargetBillableHours,
          actualBillableHours: v.actualBillableHours,
          actualTotalClientHours: v.actualTotalClientHours,
          actualTotalProductiveHours: v.actualTotalProductiveHours,
          actualTotalProjectHours: v.actualTotalProjectHours,

          actualBillableUtilization: metrics.actualBillableUtilization,
          actualClientUtilization: metrics.actualClientUtilization,
          actualProductiveUtilization: metrics.actualProductiveUtilization,
          actualTotalUtilization: metrics.actualTotalUtilization,
          actualTargetAttainment: metrics.actualTargetAttainment,

          plannedCapacity: metrics.plannedCapacity,
          plannedTargetBillableHours: metrics.plannedTargetBillableHours,
          plannedBillableHours: v.plannedBillableHours,
          plannedTotalClientHours: v.plannedTotalClientHours,
          plannedTotalProductiveHours: v.plannedTotalProductiveHours,
          plannedTotalProjectHours: v.plannedTotalProjectHours,

          plannedBillableUtilization: metrics.plannedBillableUtilization,
          plannedClientUtilization: metrics.plannedClientUtilization,
          plannedProductiveUtilization: metrics.plannedProductiveUtilization,
          plannedTotalUtilization: metrics.plannedTotalUtilization,
          plannedTargetAttainment: metrics.plannedTargetAttainment,

          billableUtilizationVariance: metrics.billableUtilizationVariance,
          clientUtilizationVariance: metrics.clientUtilizationVariance,
          productiveUtilizationVariance: metrics.productiveUtilizationVariance,
          totalUtilizationVariance: metrics.totalUtilizationVariance,
          targetAttainmentVariance: metrics.targetAttainmentVariance,

          billableUtilizationVarianceRatio: metrics.billableUtilizationVarianceRatio,
          clientUtilizationVarianceRatio: metrics.clientUtilizationVarianceRatio,
          productiveUtilizationVarianceRatio: metrics.productiveUtilizationVarianceRatio,
          totalUtilizationVarianceRatio: metrics.totalUtilizationVarianceRatio,
          targetAttainmentVarianceRatio: metrics.targetAttainmentVarianceRatio,
        };

        // Periods
        if (!a.periods[v.date])
          a.periods[v.date] = {
            actualCapacity: 0,
            actualTargetBillableHours: 0,
            actualBillableHours: 0,
            actualTotalClientHours: 0,
            actualTotalProductiveHours: 0,
            actualTotalProjectHours: 0,
            get actualBillableUtilization() {
              if (!this.actualCapacity) return null;
              return this.actualBillableHours / this.actualCapacity;
            },
            get actualClientUtilization() {
              if (!this.actualCapacity) return null;
              return this.actualTotalClientHours / this.actualCapacity;
            },
            get actualProductiveUtilization() {
              if (!this.actualCapacity) return null;
              return this.actualTotalProductiveHours / this.actualCapacity;
            },
            get actualTotalUtilization() {
              if (!this.actualCapacity) return null;
              return this.actualTotalProjectHours / this.actualCapacity;
            },
            get actualTargetAttainment() {
              if (!this.actualTargetBillableHours) return null;
              return this.actualBillableHours / this.actualTargetBillableHours;
            },

            plannedCapacity: 0,
            plannedTargetBillableHours: 0,
            plannedBillableHours: 0,
            plannedTotalClientHours: 0,
            plannedTotalProductiveHours: 0,
            plannedTotalProjectHours: 0,
            get plannedBillableUtilization() {
              if (!this.plannedCapacity) return null;
              return this.plannedBillableHours / this.plannedCapacity;
            },
            get plannedClientUtilization() {
              if (!this.plannedCapacity) return null;
              return this.plannedTotalClientHours / this.plannedCapacity;
            },
            get plannedProductiveUtilization() {
              if (!this.plannedCapacity) return null;
              return this.plannedTotalProductiveHours / this.plannedCapacity;
            },
            get plannedTotalUtilization() {
              if (!this.plannedCapacity) return null;
              return this.plannedTotalProjectHours / this.plannedCapacity;
            },
            get plannedTargetAttainment() {
              if (!this.plannedTargetBillableHours) return null;
              return this.plannedBillableHours / this.plannedTargetBillableHours;
            },

            get billableUtilizationVariance() {
              return this.actualBillableUtilization - this.plannedBillableUtilization;
            },
            get clientUtilizationVariance() {
              return this.actualClientUtilization - this.plannedClientUtilization;
            },
            get productiveUtilizationVariance() {
              return this.actualProductiveUtilization - this.plannedProductiveUtilization;
            },
            get totalUtilizationVariance() {
              return this.actualTotalUtilization - this.plannedTotalUtilization;
            },
            get targetAttainmentVariance() {
              return this.actualTargetAttainment - this.plannedTargetAttainment;
            },

            get billableUtilizationVarianceRatio() {
              if (!this.plannedBillableUtilization) return null;
              return this.billableUtilizationVariance / this.plannedBillableUtilization;
            },
            get clientUtilizationVarianceRatio() {
              if (!this.plannedClientUtilization) return null;
              return this.clientUtilizationVariance / this.plannedClientUtilization;
            },
            get productiveUtilizationVarianceRatio() {
              if (!this.plannedProductiveUtilization) return null;
              return this.productiveUtilizationVariance / this.plannedProductiveUtilization;
            },
            get totalUtilizationVarianceRatio() {
              if (!this.plannedTotalUtilization) return null;
              return this.totalUtilizationVariance / this.plannedTotalUtilization;
            },
            get targetAttainmentVarianceRatio() {
              if (!this.plannedTargetAttainment) return null;
              return this.targetAttainmentVariance / this.plannedTargetAttainment;
            },
          };
        a.periods[v.date].actualCapacity += metrics.actualCapacity || 0;
        a.periods[v.date].actualTargetBillableHours += metrics.actualTargetBillableHours || 0;
        a.periods[v.date].actualBillableHours += v.actualBillableHours || 0;
        a.periods[v.date].actualTotalClientHours += v.actualTotalClientHours || 0;
        a.periods[v.date].actualTotalProductiveHours += v.actualTotalProductiveHours || 0;
        a.periods[v.date].actualTotalProjectHours += v.actualTotalProjectHours || 0;
        a.periods[v.date].plannedCapacity += metrics.plannedCapacity || 0;
        a.periods[v.date].plannedTargetBillableHours += metrics.plannedTargetBillableHours || 0;
        a.periods[v.date].plannedBillableHours += v.plannedBillableHours || 0;
        a.periods[v.date].plannedTotalClientHours += v.plannedTotalClientHours || 0;
        a.periods[v.date].plannedTotalProductiveHours += v.plannedTotalProductiveHours || 0;
        a.periods[v.date].plannedTotalProjectHours += v.plannedTotalProjectHours || 0;

        return a;
      },
      {
        members: {},
        periods: {},
        total: {
          actualCapacity: 0,
          actualTargetBillableHours: 0,
          actualBillableHours: 0,
          actualTotalClientHours: 0,
          actualTotalProductiveHours: 0,
          actualTotalProjectHours: 0,
          get actualBillableUtilization() {
            if (!this.actualCapacity) return null;
            return this.actualBillableHours / this.actualCapacity;
          },
          get actualClientUtilization() {
            if (!this.actualCapacity) return null;
            return this.actualTotalClientHours / this.actualCapacity;
          },
          get actualProductiveUtilization() {
            if (!this.actualCapacity) return null;
            return this.actualTotalProductiveHours / this.actualCapacity;
          },
          get actualTotalUtilization() {
            if (!this.actualCapacity) return null;
            return this.actualTotalProjectHours / this.actualCapacity;
          },
          get actualTargetAttainment() {
            if (!this.actualTargetBillableHours) return null;
            return this.actualBillableHours / this.actualTargetBillableHours;
          },

          plannedCapacity: 0,
          plannedTargetBillableHours: 0,
          plannedBillableHours: 0,
          plannedTotalClientHours: 0,
          plannedTotalProductiveHours: 0,
          plannedTotalProjectHours: 0,
          get plannedBillableUtilization() {
            if (!this.plannedCapacity) return null;
            return this.plannedBillableHours / this.plannedCapacity;
          },
          get plannedClientUtilization() {
            if (!this.plannedCapacity) return null;
            return this.plannedTotalClientHours / this.plannedCapacity;
          },
          get plannedProductiveUtilization() {
            if (!this.plannedCapacity) return null;
            return this.plannedTotalProductiveHours / this.plannedCapacity;
          },
          get plannedTotalUtilization() {
            if (!this.plannedCapacity) return null;
            return this.plannedTotalProjectHours / this.plannedCapacity;
          },
          get plannedTargetAttainment() {
            if (!this.plannedTargetBillableHours) return null;
            return this.plannedBillableHours / this.plannedTargetBillableHours;
          },

          get billableUtilizationVariance() {
            return this.actualBillableUtilization - this.plannedBillableUtilization;
          },
          get clientUtilizationVariance() {
            return this.actualClientUtilization - this.plannedClientUtilization;
          },
          get productiveUtilizationVariance() {
            return this.actualProductiveUtilization - this.plannedProductiveUtilization;
          },
          get totalUtilizationVariance() {
            return this.actualTotalUtilization - this.plannedTotalUtilization;
          },
          get targetAttainmentVariance() {
            return this.actualTargetAttainment - this.plannedTargetAttainment;
          },

          get billableUtilizationVarianceRatio() {
            if (!this.plannedBillableUtilization) return null;
            return this.billableUtilizationVariance / this.plannedBillableUtilization;
          },
          get clientUtilizationVarianceRatio() {
            if (!this.plannedClientUtilization) return null;
            return this.clientUtilizationVariance / this.plannedClientUtilization;
          },
          get productiveUtilizationVarianceRatio() {
            if (!this.plannedProductiveUtilization) return null;
            return this.productiveUtilizationVariance / this.plannedProductiveUtilization;
          },
          get totalUtilizationVarianceRatio() {
            if (!this.plannedTotalUtilization) return null;
            return this.totalUtilizationVariance / this.plannedTotalUtilization;
          },
          get targetAttainmentVarianceRatio() {
            if (!this.plannedTargetAttainment) return null;
            return this.targetAttainmentVariance / this.plannedTargetAttainment;
          },
        },
      },
    );

    let start = params.period.start;
    let end = params.period.end;

    switch (params.unit) {
      case 'week':
        start = moment(start).startOf('isoWeek').format(dateFormats.isoDate);
        end = moment(end).endOf('isoWeek').format(dateFormats.isoDate);
        break;

      case 'month':
        start = moment(start).startOf('month').format(dateFormats.isoDate);
        end = moment(end).endOf('month').format(dateFormats.isoDate);
        break;
    }

    const periodCount = moment(end).diff(start, params.unit) + 1;
    const periods = [];
    for (let index = 0; index < periodCount; index++) {
      periods.push(moment(start).add(index, params.unit).format(dateFormats.isoDate));
    }

    return { members, records, periods };
  }, [report, params.period.start, params.period.end, params.unit, params.utilizationMode]);

  const { metric } = params;

  return (
    <Report.Table>
      <Table>
        <Table.Header>
          <Table.Column sticky minWidth="16rem">
            Member
          </Table.Column>

          {periods.map((p) => (
            <Table.Column key={p} align="right" width="7rem">
              <DateTime value={p} />
            </Table.Column>
          ))}

          <Table.Column align="right" width="7rem">
            Total
          </Table.Column>
        </Table.Header>

        <Table.Body>
          {members.map((member) => {
            return (
              <Table.Row key={member.id}>
                <Table.Cell>{member.name}</Table.Cell>

                {periods.map((period) => {
                  const value = records.members[member.id]?.periods[period];

                  return (
                    <Table.Cell key={period}>
                      <CellValue value={value} metric={metric} />
                    </Table.Cell>
                  );
                })}

                <Table.Cell>
                  <CellValue value={records.members[member.id]?.total} metric={metric} />
                </Table.Cell>
              </Table.Row>
            );
          })}

          <Table.Row style={{ fontWeight: weights.bold }}>
            <Table.Cell>Total</Table.Cell>

            {periods.map((period) => {
              const value = records.periods[period];

              return (
                <Table.Cell key={period}>
                  <CellValue value={value} metric={metric} />
                </Table.Cell>
              );
            })}

            <Table.Cell>
              <CellValue value={records.total} metric={metric} />
            </Table.Cell>
          </Table.Row>
        </Table.Body>

        <Table.Status total={members.length} label="Member" isLoading={query.status !== 'ready'} />
      </Table>
    </Report.Table>
  );
}

function Filters({ values, isOpen, onClose, onApply }) {
  const [filters, setFilters] = useState(values);

  const handleApply = () => {
    onApply(filters);
  };

  const handleFilter = (filter) => {
    setFilters({ ...filters, ...filter });
  };

  const handleCancel = () => {
    setFilters(values);
    onClose();
  };

  useEffect(() => {
    setFilters(values);
  }, [values]);

  return (
    <Report.FiltersDrawer isOpen={isOpen} onApply={handleApply} onClose={handleCancel}>
      <UnitFilter
        value={filters.unit}
        onChange={({ target: { value } }) =>
          handleFilter({
            unit: value,
            period: {
              day: intervalOptions.past_30_days,
              week: intervalOptions.past_12_weeks,
              month: intervalOptions.past_6_months,
            }[value],
          })
        }
      />

      <PeriodFilter
        clearable={false}
        scope={filters.unit}
        intervals={intervalsByScope[filters.unit]}
        placeholder="Date Range"
        value={filters.period}
        onChange={({ target: { value } }) => handleFilter({ period: value })}
      />

      <MemberFiltersGroup filters={filters} onChange={handleFilter} />
    </Report.FiltersDrawer>
  );
}

function PercentValue({ value }) {
  return (
    <span
      style={{
        color: value < 0 ? colors.danger : colors.black,
      }}>
      <Percent value={value} />
    </span>
  );
}

function CellValue({ value, metric }) {
  if (!value) return <Percent value={null} />;

  return (
    <ListTooltip
      message={
        <List style={{ width: '40rem' }}>
          <List.Item>
            <List.Heading>Actuals</List.Heading>
            <List.Heading>Planned</List.Heading>
            <List.Heading>Variance</List.Heading>
            <List.Heading>
              <nobr>Variance %</nobr>
            </List.Heading>
          </List.Item>

          <List.Item label="Billable Utilization">
            <PercentValue value={value.actualBillableUtilization} />
            <PercentValue value={value.plannedBillableUtilization} />
            <PercentValue value={value.billableUtilizationVariance} />
            <PercentValue value={value.billableUtilizationVarianceRatio} />
          </List.Item>

          <List.Item label="Client Utilization">
            <PercentValue value={value.actualClientUtilization} />
            <PercentValue value={value.plannedClientUtilization} />
            <PercentValue value={value.clientUtilizationVariance} />
            <PercentValue value={value.clientUtilizationVarianceRatio} />
          </List.Item>

          <List.Item label="Productive Utilization">
            <PercentValue value={value.actualProductiveUtilization} />
            <PercentValue value={value.plannedProductiveUtilization} />
            <PercentValue value={value.productiveUtilizationVariance} />
            <PercentValue value={value.productiveUtilizationVarianceRatio} />
          </List.Item>

          <List.Item label="Total Utilization">
            <PercentValue value={value.actualTotalUtilization} />
            <PercentValue value={value.plannedTotalUtilization} />
            <PercentValue value={value.totalUtilizationVariance} />
            <PercentValue value={value.totalUtilizationVarianceRatio} />
          </List.Item>

          <List.Item label="Target Attainment">
            <PercentValue value={value.actualTargetAttainment} />
            <PercentValue value={value.plannedTargetAttainment} />
            <PercentValue value={value.targetAttainmentVariance} />
            <PercentValue value={value.targetAttainmentVarianceRatio} />
          </List.Item>
        </List>
      }>
      <PercentValue
        value={
          {
            billable_utilization_variance: value.billableUtilizationVariance,
            client_utilization_variance: value.clientUtilizationVariance,
            productive_utilization_variance: value.productiveUtilizationVariance,
            total_utilization_variance: value.totalUtilizationVariance,
            target_attainment_variance: value.targetAttainmentVariance,
          }[metric || 'billable_utilization_variance']
        }
      />
    </ListTooltip>
  );
}

function MetricFilter({ value, onChange }) {
  const options = [
    { id: 'billable_utilization_variance', name: 'Billable Utilization Variance' },
    { id: 'client_utilization_variance', name: 'Client Utilization Variance' },
    { id: 'productive_utilization_variance', name: 'Productive Utilization Variance' },
    { id: 'total_utilization_variance', name: 'Total Utilization Variance' },
    { id: 'target_attainment_variance', name: 'Target Attainment Variance' },
  ];

  return (
    <SingleSelectFilter
      icon="gear"
      placeholder="Metric"
      clearable={false}
      value={value}
      renderValue={(value) => value.name}
      options={options}
      onChange={onChange}
    />
  );
}

function UnitFilter(props) {
  return (
    <SingleSelectFilter
      icon="gear"
      placeholder="Unit"
      clearable={false}
      renderValue={(value) => value.name}
      options={[
        { id: 'month', name: 'Month' },
        { id: 'week', name: 'Week' },
      ]}
      {...props}
    />
  );
}
